import api from "@/services/axios";

export type ApiError = {
  code?: string | null;
  message?: string | null;
  type?: string | null;
  target?: string | null;
  details?: Record<string, string[]> | null;
};

type ApiResponseMeta = {
  statusCode?: number;
  traceId?: string | null;
  timestamp?: string;
};

type ApiResponse<T> = {
  data?: T;
  meta?: ApiResponseMeta;
  errors?: ApiError[] | null;
  success?: boolean;
  message?: string;
};

function unwrapApiResponse<T>(payload: ApiResponse<T>): T {
  if (payload?.success === false) {
    throw new Error(payload.message || "Request failed");
  }

  const firstError = payload?.errors?.[0]?.message;
  if (firstError) {
    throw new Error(firstError);
  }

  if (payload?.data === undefined) {
    throw new Error(payload?.message || "Unexpected API response");
  }

  return payload.data;
}

export type AcademicYearDto = {
  id: string;
  name: string;
  startDate: string;
  endDate: string;
  isActive: boolean;
  createdAt: string;
};

export type CreateAcademicYearRequest = {
  name: string;
  startDate: string; // ISO or YYYY-MM-DD
  endDate: string; // ISO or YYYY-MM-DD
  isActive: boolean;
};

export type UpdateAcademicYearRequest = {
  name: string;
  startDate: string;
  endDate: string;
};

export async function fetchAcademicYears(): Promise<AcademicYearDto[]> {
  const resp = await api.get<ApiResponse<AcademicYearDto[]>>("/academic-years");
  return unwrapApiResponse(resp.data) ?? [];
}

export async function createAcademicYear(
  input: CreateAcademicYearRequest,
): Promise<string> {
  const resp = await api.post<ApiResponse<string>>("/academic-years", input);
  return unwrapApiResponse(resp.data);
}

export async function updateAcademicYear(
  id: string,
  input: UpdateAcademicYearRequest,
): Promise<boolean> {
  const resp = await api.put<ApiResponse<boolean>>(`/academic-years/${id}`, input);
  return unwrapApiResponse(resp.data);
}

export async function activateAcademicYear(id: string): Promise<boolean> {
  const resp = await api.post<ApiResponse<boolean>>(`/academic-years/${id}/activate`);
  return unwrapApiResponse(resp.data);
}

export async function deleteAcademicYear(id: string): Promise<boolean> {
  const resp = await api.delete<ApiResponse<boolean>>(`/academic-years/${id}`);
  return unwrapApiResponse(resp.data);
}
